import { apiClient } from './apiClient';
import type {
  EmployeeWorkflowAssignment,
  AssignWorkflowRequest,
  BulkAssignWorkflowRequest,
  BulkAssignWorkflowResponse,
  WorkflowAssignmentHistory,
  ApprovalWorkflow,
} from '@/types/workflow-assignment';

/**
 * Get the workflow assignment for an employee
 */
export async function getEmployeeWorkflowAssignment(
  employeeId: string
): Promise<EmployeeWorkflowAssignment> {
  return await apiClient.get<EmployeeWorkflowAssignment>(
    `/employees/${employeeId}/approval-workflow`
  );
}

/**
 * Assign a workflow to an employee
 */
export async function assignWorkflowToEmployee(
  employeeId: string,
  request: AssignWorkflowRequest
): Promise<{ success: boolean; message: string; assignment: EmployeeWorkflowAssignment }> {
  return await apiClient.post<{
    success: boolean;
    message: string;
    assignment: EmployeeWorkflowAssignment;
  }>(`/employees/${employeeId}/approval-workflow`, request);
}

/**
 * Get workflow assignment history for an employee
 */
export async function getWorkflowAssignmentHistory(
  employeeId: string
): Promise<WorkflowAssignmentHistory[]> {
  return await apiClient.get<WorkflowAssignmentHistory[]>(
    `/employees/${employeeId}/workflow-history`
  );
}

/**
 * Bulk assign workflow to multiple employees
 */
export async function bulkAssignWorkflow(
  request: BulkAssignWorkflowRequest
): Promise<BulkAssignWorkflowResponse> {
  return await apiClient.post<BulkAssignWorkflowResponse>(
    '/employees/bulk-assign-workflow',
    request
  );
}

/**
 * Get all available workflows
 */
export async function getAllWorkflows(): Promise<ApprovalWorkflow[]> {
  return await apiClient.get<ApprovalWorkflow[]>('/workflows');
}

/**
 * Get the default workflow
 */
export async function getDefaultWorkflow(): Promise<ApprovalWorkflow | null> {
  const workflows = await getAllWorkflows();
  return workflows.find((w) => w.isDefault) || null;
}

/**
 * Helper function to determine workflow type
 */
export function getWorkflowType(
  assignment: EmployeeWorkflowAssignment
): 'default' | 'custom' | 'no-approval' {
  if (assignment.workflowId === 'NO_APPROVAL') {
    return 'no-approval';
  }
  if (assignment.isDefault || !assignment.workflowId) {
    return 'default';
  }
  return 'custom';
}

/**
 * Helper function to get workflow icon
 */
export function getWorkflowIcon(type: 'default' | 'custom' | 'no-approval'): string {
  switch (type) {
    case 'default':
      return '🔄';
    case 'custom':
      return '⚙️';
    case 'no-approval':
      return '✅';
  }
}

/**
 * Helper function to get workflow badge color
 */
export function getWorkflowBadgeColor(
  type: 'default' | 'custom' | 'no-approval'
): string {
  switch (type) {
    case 'default':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'custom':
      return 'bg-purple-100 text-purple-800 border-purple-300';
    case 'no-approval':
      return 'bg-green-100 text-green-800 border-green-300';
  }
}

/**
 * Helper function to get workflow card color
 */
export function getWorkflowCardColor(
  type: 'default' | 'custom' | 'no-approval'
): string {
  switch (type) {
    case 'default':
      return 'border-blue-200 bg-blue-50/50';
    case 'custom':
      return 'border-purple-200 bg-purple-50/50';
    case 'no-approval':
      return 'border-green-200 bg-green-50/50';
  }
}
